import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, Radio, RefreshCw } from 'lucide-react';
import {
  listLscLiveDashboard,
  mapLiveDashboardRowToLeagueGame,
  resolveLiveStreamKey,
} from '../api';
import type { LscLiveLeagueGame } from '../types';
import { parseTwitchChannelLogin } from '../twitchEmbed';
import LiveGameEmbedPanel, {
  formatLiveGameBroadcasterLabel,
  formatLiveGameMatchupLabel,
} from './LiveGameEmbedPanel';
import { useLeagueHomeLivePolling } from '../hooks/useLeagueHomeLivePolling';
import TeamMatchupBadge from './leagueHome/TeamMatchupBadge';
import { displaySeasonLabel } from '../seasonDisplay';

const MEMBER_VISIBLE_STATUSES = new Set([
  'verified_live_game',
  'verified',
  'qualified_likely_game',
  'verified_match_side_pending',
]);
const REVIEW_QUEUE_STATUSES = new Set(['needs_commissioner_review', 'raw_member_stream_detected']);
const SIDE_REVIEW_STATUSES = new Set(['needs_side_review', 'side_uncertain']);
const SCHEDULE_CONTEXT_REVIEW_STATUSES = new Set([
  'stale',
  'missing',
  'ambiguous',
  'conflicting',
  'unknown',
]);
const SCHEDULE_REVIEW_ACTIONS = new Set(['sync_schedule', 'check_schedule_sync', 'check_readiness']);

export type ReviewRowSubtype =
  | 'verified_match_side_pending'
  | 'side_review'
  | 'schedule_or_setup_review'
  | 'raw_stream'
  | 'generic_review';

export type ReviewRowPresentation = {
  subtype: ReviewRowSubtype;
  label: string;
  copy: string;
  actionChip: string;
};

function normalizeReviewReasons(game: LscLiveLeagueGame): string[] {
  return (game.review_reasons ?? []).map((reason) => String(reason).trim().toLowerCase());
}

function hasSideReviewSignal(game: LscLiveLeagueGame): boolean {
  const truthStatus = String(game.live_truth_status ?? '').trim().toLowerCase();
  const reasons = normalizeReviewReasons(game);
  return (
    SIDE_REVIEW_STATUSES.has(truthStatus) ||
    reasons.includes('broadcaster_side_review_required') ||
    reasons.some(
      (reason) =>
        reason.includes('side_review') ||
        reason.includes('broadcaster_side') ||
        reason.includes('visual_inference_conflicts_with_member_assignment')
    )
  );
}

function hasScheduleOrSetupReviewSignal(game: LscLiveLeagueGame): boolean {
  const truthStatus = String(game.live_truth_status ?? '').trim().toLowerCase();
  const scheduleContext = String(game.schedule_context_status ?? '').trim().toLowerCase();
  const recommendedAction = String(game.recommended_action ?? '').trim().toLowerCase();
  const reasons = normalizeReviewReasons(game);
  return (
    truthStatus === 'needs_schedule_sync' ||
    SCHEDULE_CONTEXT_REVIEW_STATUSES.has(scheduleContext) ||
    SCHEDULE_REVIEW_ACTIONS.has(recommendedAction) ||
    reasons.some(
      (reason) =>
        reason.startsWith('schedule_context_') ||
        reason.includes('schedule_stale') ||
        reason.includes('schedule_missing') ||
        reason.includes('schedule_ambiguous') ||
        reason.includes('schedule_conflicting') ||
        reason.includes('missing_live_truth') ||
        reason.includes('readiness') ||
        reason.includes('setup')
    )
  );
}

function isVerifiedMatchSidePending(game: LscLiveLeagueGame): boolean {
  const tier = String(game.dashboard_tier ?? '').trim().toLowerCase();
  const canonical = String(game.canonical_state ?? '').trim().toLowerCase();
  return tier === 'verified_match_side_pending' || canonical === 'verified_side_pending';
}

export function classifyReviewRowSubtype(game: LscLiveLeagueGame): ReviewRowSubtype {
  const status = String(game.verification_status ?? '').trim().toLowerCase();
  if (status === 'raw_member_stream_detected') {
    return 'raw_stream';
  }
  if (isVerifiedMatchSidePending(game)) {
    return 'verified_match_side_pending';
  }
  if (hasSideReviewSignal(game)) {
    return 'side_review';
  }
  if (hasScheduleOrSetupReviewSignal(game)) {
    return 'schedule_or_setup_review';
  }
  return 'generic_review';
}

export function getReviewRowPresentation(game: LscLiveLeagueGame): ReviewRowPresentation {
  const subtype = classifyReviewRowSubtype(game);
  switch (subtype) {
    case 'raw_stream':
      return {
        subtype,
        label: 'Raw stream',
        copy: 'Member is live, but this stream has not been qualified as a league game yet.',
        actionChip: 'Wait for live game detection',
      };
    case 'verified_match_side_pending':
      return {
        subtype,
        label: 'Verified match · side pending',
        copy: 'LSC verified the scheduled game, but broadcaster side still needs confirmation.',
        actionChip: 'Confirm side in live game detection',
      };
    case 'side_review':
      return {
        subtype,
        label: 'Side review',
        copy: 'Game appears to be the scheduled matchup, but broadcaster side needs confirmation.',
        actionChip: 'Confirm side in live game detection',
      };
    case 'schedule_or_setup_review':
      return {
        subtype,
        label: 'Schedule/setup review',
        copy: 'League context needs attention before this stream can be qualified.',
        actionChip: 'Check schedule sync or readiness',
      };
    default:
      return {
        subtype: 'generic_review',
        label: 'Needs review',
        copy: 'This stream needs commissioner review before it can appear as a qualified league game.',
        actionChip: 'Review details',
      };
  }
}

export function getReviewRowCopy(game: LscLiveLeagueGame): string {
  return getReviewRowPresentation(game).copy;
}

export function isSafeTwitchWatchUrl(url: string | null | undefined): boolean {
  const trimmed = url?.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:') return false;
    if (!parsed.hostname.toLowerCase().endsWith('twitch.tv')) return false;
    return parseTwitchChannelLogin(trimmed) != null;
  } catch {
    return false;
  }
}

export type LiveLeagueGamesCardProps = {
  leagueId: string;
};

export type LiveLeagueGamesCardViewProps = {
  qualifiedGames: LscLiveLeagueGame[];
  reviewGames: LscLiveLeagueGame[];
  viewerIsCommissioner: boolean;
  loading: boolean;
  error: string | null;
  selectedLiveStreamKey: string | null;
  onRefresh: () => void;
  onSelectGame: (liveStreamKey: string) => void;
};

export function isVerifiedLiveGame(game: LscLiveLeagueGame): boolean {
  const status = String(game.verification_status ?? '').trim().toLowerCase();
  return status === 'verified_live_game' || status === 'verified';
}

export function isPendingLiveVerification(game: LscLiveLeagueGame): boolean {
  const tier = String(game.dashboard_tier ?? game.verification_status ?? '')
    .trim()
    .toLowerCase();
  return tier === 'verified_match_side_pending';
}

export function isQualifiedLikelyLiveGame(game: LscLiveLeagueGame): boolean {
  const status = String(game.verification_status ?? '').trim().toLowerCase();
  return status === 'qualified_likely_game' || isPendingLiveVerification(game);
}

export function isQualifiedMainGame(game: LscLiveLeagueGame): boolean {
  const status = String(game.verification_status ?? '').trim().toLowerCase();
  return MEMBER_VISIBLE_STATUSES.has(status);
}

export function isReviewQueueGame(game: LscLiveLeagueGame): boolean {
  const status = String(game.verification_status ?? '').trim().toLowerCase();
  return REVIEW_QUEUE_STATUSES.has(status);
}

function isCandidateLiveGame(game: LscLiveLeagueGame): boolean {
  return String(game.verification_status ?? '').trim().toLowerCase() === 'candidate';
}

export function getGamesSubcopy(): string {
  return 'Qualified league games detected from connected broadcaster streams.';
}

export function liveStreamKeyTestId(liveStreamKey: string): string {
  return liveStreamKey.replace(/[^a-zA-Z0-9_-]+/g, '-');
}

export function partitionDashboardGames(
  rows: LscLiveLeagueGame[],
  options: { viewerIsCommissioner: boolean; memberVisibleRows?: LscLiveLeagueGame[] }
): { qualifiedGames: LscLiveLeagueGame[]; reviewGames: LscLiveLeagueGame[] } {
  const qualifiedFromRows = rows.filter(isQualifiedMainGame);
  const reviewGames = options.viewerIsCommissioner ? rows.filter(isReviewQueueGame) : [];

  if (options.viewerIsCommissioner) {
    return { qualifiedGames: qualifiedFromRows, reviewGames };
  }

  const memberVisible = (options.memberVisibleRows ?? []).filter(isQualifiedMainGame);
  const qualifiedGames = memberVisible.length > 0 ? memberVisible : qualifiedFromRows;
  return { qualifiedGames, reviewGames: [] };
}

type StatusBadge = {
  label: string;
  className: string;
  testId: string;
};

export function isScheduleCorrectedLiveGame(game: LscLiveLeagueGame): boolean {
  return Boolean(game.schedule_corrected);
}

export function getLiveGameStatusBadge(game: LscLiveLeagueGame): StatusBadge | null {
  if (isScheduleCorrectedLiveGame(game)) {
    return {
      label: game.schedule_corrected_status_label || 'Commissioner approved',
      className:
        'rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-200',
      testId: 'live-league-game-schedule-corrected-label',
    };
  }
  if (isVerifiedLiveGame(game)) {
    return {
      label: 'Verified Live',
      className:
        'rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-200',
      testId: 'live-league-game-verified-label',
    };
  }
  if (isPendingLiveVerification(game)) {
    return {
      label: 'Verification in progress',
      className:
        'rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-200',
      testId: 'live-league-game-pending-label',
    };
  }
  if (isQualifiedLikelyLiveGame(game)) {
    return {
      label: 'Likely Live',
      className:
        'rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs text-sky-200',
      testId: 'live-league-game-likely-label',
    };
  }
  if (isCandidateLiveGame(game)) {
    return {
      label: 'Candidate',
      className:
        'rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200',
      testId: 'live-league-game-candidate-label',
    };
  }
  return null;
}

export function formatVerificationConfidence(game: LscLiveLeagueGame): string | null {
  const confidence = game.verification_confidence;
  if (confidence == null || Number.isNaN(Number(confidence))) return null;
  const numeric = Number(confidence);
  const pct = numeric <= 1 ? Math.round(numeric * 100) : Math.round(numeric);
  if (isVerifiedLiveGame(game)) {
    return `${pct}% verified`;
  }
  if (isPendingLiveVerification(game) || isQualifiedLikelyLiveGame(game)) {
    return `${pct}% likely`;
  }
  return null;
}

function formatStageSeasonLabel(game: LscLiveLeagueGame): string | null {
  const parts = [
    game.stage_label?.trim(),
    displaySeasonLabel(game.season_label),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatBroadcasterSide(side: LscLiveLeagueGame['broadcaster_side']): string | null {
  const normalized = String(side ?? '').trim().toLowerCase();
  if (normalized === 'home' || normalized === 'away') {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  return null;
}

function LiveGameStatusBadge({ game }: { game: LscLiveLeagueGame }) {
  const badge = getLiveGameStatusBadge(game);
  if (!badge) return null;
  return (
    <span className={badge.className} data-testid={badge.testId}>
      {badge.label}
    </span>
  );
}

function LiveGameVerificationDetails({ game }: { game: LscLiveLeagueGame }) {
  const confidenceLabel = formatVerificationConfidence(game);
  const evidenceSummary =
    isVerifiedLiveGame(game) || isQualifiedLikelyLiveGame(game) || isPendingLiveVerification(game)
      ? game.evidence_summary?.trim()
      : null;
  if (!confidenceLabel && !evidenceSummary) return null;
  return (
    <div className="space-y-1" data-testid="live-league-game-verification-details">
      {confidenceLabel ? (
        <p className="text-xs text-emerald-300/90" data-testid="live-league-game-confidence">
          {confidenceLabel}
        </p>
      ) : null}
      {evidenceSummary ? (
        <p className="text-xs text-secondary-500" data-testid="live-league-game-evidence-summary">
          {evidenceSummary}
        </p>
      ) : null}
    </div>
  );
}

function LiveGameRowMeta({ game }: { game: LscLiveLeagueGame }) {
  const broadcaster = formatLiveGameBroadcasterLabel(game);
  const side = formatBroadcasterSide(game.broadcaster_side);
  const stageSeason = formatStageSeasonLabel(game);
  return (
    <>
      <p className="text-sm text-secondary-400">
        {broadcaster}
        {side ? ` · ${side}` : ''}
        {stageSeason ? ` · ${stageSeason}` : ''}
      </p>
      <LiveGameVerificationDetails game={game} />
    </>
  );
}

function LiveGameMatchupVisual({ game }: { game: LscLiveLeagueGame }) {
  if (game.matchup_display_mode === 'neutral' || game.matchup_label_suppressed) {
    return (
      <span className="font-medium text-white">Live verification in progress</span>
    );
  }

  const awayLabel = game.away_team?.trim() || 'Away';
  const homeLabel = game.home_team?.trim() || 'Home';

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="live-league-game-matchup-badges">
      <TeamMatchupBadge label={awayLabel} side="away" size="sm" />
      <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">vs</span>
      <TeamMatchupBadge label={homeLabel} side="home" size="sm" />
    </div>
  );
}

function QualifiedGameRow({
  game,
  isSelected,
  onSelect,
}: {
  game: LscLiveLeagueGame;
  isSelected: boolean;
  onSelect: (liveStreamKey: string) => void;
}) {
  const streamKey = resolveLiveStreamKey(game);
  const matchup = formatLiveGameMatchupLabel(game);
  return (
    <button
      type="button"
      onClick={() => onSelect(streamKey)}
      className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-500/10 ring-1 ring-primary-500/40'
          : 'border-secondary-700 bg-secondary-800/40 hover:border-secondary-600'
      }`}
      aria-pressed={isSelected}
      aria-label={`Select live league game ${matchup}`}
      data-testid={`live-league-game-select-${liveStreamKeyTestId(streamKey)}`}
      data-selected={isSelected ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Radio
          className={`h-4 w-4 ${isSelected ? 'text-primary-400' : 'text-secondary-500'}`}
          aria-hidden
        />
        <LiveGameMatchupVisual game={game} />
        <LiveGameStatusBadge game={game} />
      </div>
      <div className="mt-1 space-y-1">
        <LiveGameRowMeta game={game} />
      </div>
    </button>
  );
}

function ReviewGameRow({ game }: { game: LscLiveLeagueGame }) {
  const streamKey = resolveLiveStreamKey(game);
  const presentation = getReviewRowPresentation(game);
  const watchUrl = isSafeTwitchWatchUrl(game.watch_url) ? game.watch_url!.trim() : null;

  return (
    <div
      className="rounded-lg border border-secondary-700/80 bg-secondary-900/40 px-4 py-3 space-y-2"
      data-testid={`live-league-game-review-${liveStreamKeyTestId(streamKey)}`}
      data-review-subtype={presentation.subtype}
    >
      <div className="flex flex-wrap items-center gap-2">
        <LiveGameMatchupVisual game={game} />
        <span
          className="rounded-full border border-secondary-600 bg-secondary-800 px-2 py-0.5 text-xs text-secondary-300"
          data-testid="live-league-game-review-label"
        >
          {presentation.label}
        </span>
        <span
          className="rounded-full border border-secondary-700/80 bg-secondary-900 px-2 py-0.5 text-xs text-secondary-400"
          data-testid="live-league-game-review-action-chip"
        >
          {presentation.actionChip}
        </span>
      </div>
      <p className="text-sm text-secondary-400">{formatLiveGameBroadcasterLabel(game)}</p>
      <p className="text-xs text-secondary-500" data-testid="live-league-game-review-copy">
        {presentation.copy}
      </p>
      <p className="text-xs text-secondary-500" data-testid="live-league-game-review-truth-hint">
        Details are available in live game detection above.
      </p>
      {watchUrl ? (
        <a
          href={watchUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary-300 hover:text-primary-200"
          data-testid="live-league-game-review-open-stream"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Open stream
        </a>
      ) : null}
    </div>
  );
}

export function LiveLeagueGamesCardView({
  qualifiedGames,
  reviewGames,
  viewerIsCommissioner,
  loading,
  error,
  selectedLiveStreamKey,
  onRefresh,
  onSelectGame,
}: LiveLeagueGamesCardViewProps) {
  const selectedGame = useMemo(
    () =>
      qualifiedGames.find((game) => resolveLiveStreamKey(game) === selectedLiveStreamKey) ?? null,
    [qualifiedGames, selectedLiveStreamKey]
  );
  const subcopy = getGamesSubcopy();

  return (
    <div className="card space-y-4" data-testid="live-league-games-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Live League Games</h2>
          {!loading && !error ? (
            <p className="text-sm text-secondary-400 mt-1" data-testid="live-league-games-subcopy">
              {subcopy}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {qualifiedGames.length > 1 ? (
            <span className="text-xs text-secondary-400" data-testid="live-league-games-count">
              {qualifiedGames.length} live
            </span>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            aria-label="Refresh live league games"
            data-testid="live-league-games-refresh"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="live-league-games-loading">
          Checking live league games...
        </p>
      ) : error ? (
        <div className="space-y-3" data-testid="live-league-games-error">
          <p className="text-sm text-red-300">Could not load live league games. Try again.</p>
          <button
            type="button"
            onClick={onRefresh}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            aria-label="Retry loading live league games"
            data-testid="live-league-games-retry"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
        </div>
      ) : qualifiedGames.length === 0 ? (
        <div className="space-y-1" data-testid="live-league-games-empty">
          <p className="text-sm text-secondary-400">No qualified league games are live right now.</p>
          {viewerIsCommissioner ? (
            <p className="text-xs text-secondary-500">
              Commissioners can review side checks, raw streams, and setup issues below.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4" data-testid="live-league-games-list">
          {qualifiedGames.length > 1 ? (
            <div className="space-y-2" role="list" aria-label="Live league games">
              {qualifiedGames.map((game) => {
                const streamKey = resolveLiveStreamKey(game);
                return (
                  <QualifiedGameRow
                    key={streamKey}
                    game={game}
                    isSelected={streamKey === selectedLiveStreamKey}
                    onSelect={onSelectGame}
                  />
                );
              })}
            </div>
          ) : (
            <div
              className="rounded-lg border border-secondary-700 bg-secondary-800/40 p-4 space-y-2"
              data-testid={`live-league-game-row-${liveStreamKeyTestId(
                resolveLiveStreamKey(qualifiedGames[0])
              )}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <LiveGameMatchupVisual game={qualifiedGames[0]} />
                <LiveGameStatusBadge game={qualifiedGames[0]} />
              </div>
              <LiveGameRowMeta game={qualifiedGames[0]} />
            </div>
          )}

          {selectedGame ? <LiveGameEmbedPanel game={selectedGame} /> : null}
        </div>
      )}

      {viewerIsCommissioner && reviewGames.length > 0 ? (
        <div className="space-y-3 border-t border-secondary-800 pt-4" data-testid="live-league-games-review-section">
          <div>
            <h3 className="text-sm font-semibold text-secondary-200">Needs Review</h3>
            <p className="text-xs text-secondary-500 mt-1">
              Review streams are shown separately and do not appear in the qualified games list.
            </p>
          </div>
          <div className="space-y-2">
            {reviewGames.map((game) => (
              <ReviewGameRow key={resolveLiveStreamKey(game)} game={game} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const LiveLeagueGamesCard: React.FC<LiveLeagueGamesCardProps> = ({ leagueId }) => {
  const [qualifiedGames, setQualifiedGames] = useState<LscLiveLeagueGame[]>([]);
  const [reviewGames, setReviewGames] = useState<LscLiveLeagueGame[]>([]);
  const [viewerIsCommissioner, setViewerIsCommissioner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLiveStreamKey, setSelectedLiveStreamKey] = useState<string | null>(null);
  const initialLoadRef = useRef(true);

  const loadGames = useCallback(async () => {
    if (initialLoadRef.current) {
      setLoading(true);
    }
    setError(null);
    try {
      const dashboard = await listLscLiveDashboard(leagueId);
      const mappedRows = (dashboard.rows ?? []).map(mapLiveDashboardRowToLeagueGame);
      const memberVisibleRows = (dashboard.member_visible_rows ?? []).map(
        mapLiveDashboardRowToLeagueGame
      );
      const partitioned = partitionDashboardGames(mappedRows, {
        viewerIsCommissioner: Boolean(dashboard.viewer_is_commissioner),
        memberVisibleRows,
      });
      setViewerIsCommissioner(Boolean(dashboard.viewer_is_commissioner));
      setQualifiedGames(partitioned.qualifiedGames);
      setReviewGames(partitioned.reviewGames);
    } catch {
      setError('Could not load live league games. Try again.');
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
    }
  }, [leagueId]);

  useEffect(() => {
    void loadGames();
  }, [loadGames]);

  useEffect(() => {
    if (qualifiedGames.length === 0) {
      setSelectedLiveStreamKey(null);
      return;
    }
    setSelectedLiveStreamKey((current) => {
      const keys = qualifiedGames.map((game) => resolveLiveStreamKey(game));
      if (current && keys.includes(current)) {
        return current;
      }
      return keys[0] ?? null;
    });
  }, [qualifiedGames]);

  const hasLiveActivity = qualifiedGames.length > 0 || reviewGames.length > 0;
  useLeagueHomeLivePolling(() => loadGames(), hasLiveActivity);

  return (
    <LiveLeagueGamesCardView
      qualifiedGames={qualifiedGames}
      reviewGames={reviewGames}
      viewerIsCommissioner={viewerIsCommissioner}
      loading={loading}
      error={error}
      selectedLiveStreamKey={selectedLiveStreamKey}
      onRefresh={() => {
        void loadGames();
      }}
      onSelectGame={setSelectedLiveStreamKey}
    />
  );
};

export default LiveLeagueGamesCard;
